import React, { useEffect, useState } from "react";
import axios from "axios";
import { TbSquareRoundedCheck, TbSquareRoundedX } from "react-icons/tb";
import NameChangePopup from "./NameChangePopup";
import TableLoader from "Components/Common/Loader/TableLoader";
import Pagination from "Components/Pagination/Pagination";
import { usePermission } from "Context/PermissionContext";

const NameChangeReq = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.userRequests?.subModule?.nameChangeReq?.permissions;
  // Fetch requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const query = activeTab === "All" ? "" : `&status=${activeTab}`;
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/name-change-request/get-all-requests?page=${currentPage}&limit=${limit}${query}`
      );

      setRequests(res.data.requests || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [activeTab, currentPage]);

  const handleAction = (row, status) => {
    setSelectedRequest(row);
    setActionType(status);
    setShowPopup(true);
  };

  return (
    <div className="mx-auto mt-5 w-auto rounded-t-2xl">
      {/* Tabs */}
      <div className="mb-4 flex gap-4">
        {["All", "Pending", "Approved", "Rejected"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setCurrentPage(1);
            }}
            className={`rounded-lg px-4 py-2 ${
              activeTab === tab
                ? "bg-[#c51369] text-white"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-t-2xl">
        {loading ? (
          <TableLoader columns={4} />
        ) : (
          <table className="w-full rounded-t-2xl text-left text-sm">
            <thead className="text-md bg-[#e0ced7] uppercase tracking-wide text-navy-900">
              <tr className="text-center">
                <th className="px-6 py-3">Sl No.</th>
                <th className="px-6 py-3">Nick Name</th>
                <th className="px-6 py-3">Requested Nickname</th>
                <th className="px-6 py-3">Status / Action</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 text-center text-red-500"
                  >
                    {error}
                  </td>
                </tr>
              ) : requests.length > 0 ? (
                requests.map((row, index) => (
                  <tr
                    className="text-md bg-white text-center tracking-wide text-gray-700"
                    key={row._id}
                  >
                    <td className="border px-6 py-4">
                      {(currentPage - 1) * limit + index + 1}
                    </td>
                    <td className="border px-6 py-4">
                      {row.currentNickName || row.userId?.nickName || "N/A"}
                      <br />
                      <span>+91 {row.userId?.phone.slice(2)}</span>
                    </td>
                    <td className="border px-6 py-4">
                      {row.requestedNickName}
                    </td>

                    {/* Combined Status + Action Column */}
                    <td className="border px-6 py-4">
                      {row.status === "Pending" &&
                      currentTabPermissions?.edit ? (
                        <div className="flex items-center justify-center gap-3">
                          <TbSquareRoundedCheck
                            onClick={() => handleAction(row, "Approved")}
                            size={28}
                            className="cursor-pointer text-green-600 hover:text-green-800"
                            title="Approve"
                          />
                          <TbSquareRoundedX
                            onClick={() => handleAction(row, "Rejected")}
                            size={28}
                            className="cursor-pointer text-red-600 hover:text-red-800"
                            title="Reject"
                          />
                        </div>
                      ) : row.status === "Approved" ? (
                        <span className="font-medium text-green-600">
                          Approved
                        </span>
                      ) : row.status === "Rejected" ? (
                        <span className="font-medium text-red-600">
                          Rejected
                        </span>
                      ) : (
                        <span className="text-gray-500">No Action</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => setCurrentPage(page)}
        showFirstLast={true}
      />

      {/* Popup */}
      {showPopup && selectedRequest && (
        <NameChangePopup
          request={selectedRequest}
          actionType={actionType}
          onClose={() => setShowPopup(false)}
          onSuccess={() => {
            setShowPopup(false);
            fetchRequests();
          }}
        />
      )}
    </div>
  );
};

export default NameChangeReq;
